/**
 * Sistema de diseño.
 * Azul petróleo metalizado de base; los flúor son exclusivamente semánticos:
 * verde = bien · amarillo = atención · rojo = problema. Nunca decorativos.
 */

import {
  forwardRef,
  type ReactNode,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

// ── Tarjeta ───────────────────────────────────────────────────────────────
export function Tarjeta({
  className,
  children,
  ...props
}: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('metal rounded-tarjeta shadow-tarjeta border border-linea', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function TarjetaTitulo({ children, accion }: { children: ReactNode; accion?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-linea px-4 py-3">
      <h2 className="text-sm font-semibold tracking-wide text-texto">{children}</h2>
      {accion}
    </div>
  );
}

// ── Botón ─────────────────────────────────────────────────────────────────
type VarianteBoton = 'primario' | 'secundario' | 'fantasma' | 'peligro' | 'exito';

const VARIANTES: Record<VarianteBoton, string> = {
  primario:
    'bg-petroleo-400 text-white hover:bg-petroleo-300 hover:text-petroleo-900 active:scale-[.98]',
  secundario:
    'bg-petroleo-600 text-texto border border-linea hover:bg-petroleo-500 active:scale-[.98]',
  fantasma: 'text-texto-mute hover:text-texto hover:bg-white/5',
  peligro: 'bg-fluor-rojo/15 text-fluor-rojo border border-fluor-rojo/40 hover:bg-fluor-rojo/25',
  exito: 'bg-fluor-verde/15 text-fluor-verde border border-fluor-verde/40 hover:bg-fluor-verde/25',
};

export const Boton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    variante?: VarianteBoton;
    tamano?: 'sm' | 'md' | 'lg';
    icono?: ReactNode;
  }
>(function Boton(
  { className, variante = 'secundario', tamano = 'md', icono, children, ...props },
  ref,
) {
  const tamanos = {
    sm: 'h-9 px-3 text-xs gap-1.5',
    md: 'min-h-tactil px-4 text-sm gap-2',
    lg: 'min-h-[52px] px-5 text-base gap-2.5',
  };
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center rounded-control font-medium',
        'transition-colors disabled:opacity-40 disabled:pointer-events-none',
        VARIANTES[variante],
        tamanos[tamano],
        className,
      )}
      {...props}
    >
      {icono}
      {children}
    </button>
  );
});

// ── Campo de texto ────────────────────────────────────────────────────────
export const Campo = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { etiqueta?: string; error?: string; sufijo?: ReactNode }
>(function Campo({ className, etiqueta, error, sufijo, id, ...props }, ref) {
  const idCampo = id ?? props.name;
  return (
    <label className="block" htmlFor={idCampo}>
      {etiqueta && (
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
          {etiqueta}
        </span>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={idCampo}
          className={cn(
            'w-full min-h-tactil rounded-control bg-petroleo-800/60 px-3 text-sm text-texto',
            'border border-linea placeholder:text-texto-mute/50',
            'focus:border-petroleo-300 focus:outline-none focus:ring-1 focus:ring-petroleo-300',
            error && 'border-fluor-rojo/60 focus:border-fluor-rojo focus:ring-fluor-rojo',
            sufijo && 'pr-12',
            className,
          )}
          {...props}
        />
        {sufijo && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-texto-mute">
            {sufijo}
          </span>
        )}
      </div>
      {error && <span className="mt-1 block text-xs text-fluor-rojo">{error}</span>}
    </label>
  );
});

// ── Combo: elegir de la lista o escribir ──────────────────────────────────
/**
 * Un desplegable que además deja teclear.
 *
 * En planta aparecen clientes, destinos y equipos que todavía no están en el
 * catálogo, y un desplegable cerrado obliga a elegir algo falso o a dejar el
 * campo vacío. Aquí se escribe y queda el texto; si coincide con una entrada
 * del catálogo, la guía se enlaza con ella.
 *
 * Usa `<datalist>`, que es nativo: el teclado del teléfono funciona igual que
 * en cualquier campo y la lista se filtra sola al escribir.
 */
export const Combo = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'list'> & {
    etiqueta?: string;
    error?: string;
    sufijo?: ReactNode;
    /** `valor` es lo que se escribe; `detalle` sale al costado en la lista. */
    opciones: { valor: string; detalle?: string }[];
  }
>(function Combo({ className, etiqueta, error, sufijo, opciones, id, ...props }, ref) {
  const idCampo = id ?? props.name;
  const idLista = `${idCampo}-opciones`;
  return (
    <label className="block" htmlFor={idCampo}>
      {etiqueta && (
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
          {etiqueta}
        </span>
      )}
      <div className="relative">
        <input
          ref={ref}
          id={idCampo}
          list={idLista}
          autoComplete="off"
          className={cn(
            'w-full min-h-tactil rounded-control bg-petroleo-800/60 px-3 text-sm text-texto',
            'border border-linea placeholder:text-texto-mute/50',
            'focus:border-petroleo-300 focus:outline-none focus:ring-1 focus:ring-petroleo-300',
            error && 'border-fluor-rojo/60 focus:border-fluor-rojo focus:ring-fluor-rojo',
            sufijo && 'pr-12',
            className,
          )}
          {...props}
        />
        {sufijo && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-texto-mute">
            {sufijo}
          </span>
        )}
        <datalist id={idLista}>
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.detalle}
            </option>
          ))}
        </datalist>
      </div>
      {error && <span className="mt-1 block text-xs text-fluor-rojo">{error}</span>}
    </label>
  );
});

// ── Selector ──────────────────────────────────────────────────────────────
export const Selector = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & { etiqueta?: string; error?: string }
>(function Selector({ className, etiqueta, error, id, children, ...props }, ref) {
  const idCampo = id ?? props.name;
  return (
    <label className="block" htmlFor={idCampo}>
      {etiqueta && (
        <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-texto-mute">
          {etiqueta}
        </span>
      )}
      <select
        ref={ref}
        id={idCampo}
        className={cn(
          'w-full min-h-tactil rounded-control bg-petroleo-800/60 px-3 text-sm text-texto',
          'border border-linea focus:border-petroleo-300 focus:outline-none focus:ring-1 focus:ring-petroleo-300',
          error && 'border-fluor-rojo/60',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {error && <span className="mt-1 block text-xs text-fluor-rojo">{error}</span>}
    </label>
  );
});

// ── Semáforo ──────────────────────────────────────────────────────────────
export type Nivel = 'VERDE' | 'AMARILLO' | 'ROJO' | 'NEUTRO';

const NIVELES: Record<Nivel, { texto: string; fondo: string; punto: string; sombra: string }> = {
  VERDE: {
    texto: 'text-fluor-verde',
    fondo: 'bg-fluor-verde/10 border-fluor-verde/35',
    punto: 'bg-fluor-verde',
    sombra: 'shadow-glow-verde',
  },
  AMARILLO: {
    texto: 'text-fluor-amarillo',
    fondo: 'bg-fluor-amarillo/10 border-fluor-amarillo/35',
    punto: 'bg-fluor-amarillo',
    sombra: 'shadow-glow-amarillo',
  },
  ROJO: {
    texto: 'text-fluor-rojo',
    fondo: 'bg-fluor-rojo/10 border-fluor-rojo/35',
    punto: 'bg-fluor-rojo',
    sombra: 'shadow-glow-rojo',
  },
  NEUTRO: {
    texto: 'text-texto-mute',
    fondo: 'bg-white/5 border-linea',
    punto: 'bg-texto-mute',
    sombra: '',
  },
};

export function Chip({
  nivel = 'NEUTRO',
  children,
  className,
  conPunto = true,
}: {
  nivel?: Nivel;
  children: ReactNode;
  className?: string;
  conPunto?: boolean;
}) {
  const n = NIVELES[nivel];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
        n.fondo,
        n.texto,
        className,
      )}
    >
      {conPunto && <span className={cn('h-1.5 w-1.5 rounded-full', n.punto, n.sombra)} />}
      {children}
    </span>
  );
}

/** Franja de alerta persistente. Para el semáforo de contrato. */
export function Alerta({
  nivel,
  titulo,
  children,
}: {
  nivel: Nivel;
  titulo: string;
  children?: ReactNode;
}) {
  const n = NIVELES[nivel];
  return (
    <div className={cn('rounded-tarjeta border px-4 py-3', n.fondo)}>
      <div className={cn('flex items-center gap-2 text-sm font-semibold', n.texto)}>
        <span className={cn('h-2 w-2 rounded-full', n.punto, n.sombra)} />
        {titulo}
      </div>
      {children && <div className="mt-1 text-xs text-texto-mute">{children}</div>}
    </div>
  );
}

// ── Indicador numérico ────────────────────────────────────────────────────
export function Indicador({
  etiqueta,
  valor,
  unidad,
  nivel = 'NEUTRO',
  detalle,
  className,
}: {
  etiqueta: string;
  valor: ReactNode;
  unidad?: string;
  nivel?: Nivel;
  detalle?: ReactNode;
  className?: string;
}) {
  return (
    <Tarjeta className={cn('px-4 py-3', className)}>
      <div className="text-[11px] font-medium uppercase tracking-wider text-texto-mute">
        {etiqueta}
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span
          className={cn(
            'font-mono text-2xl font-semibold tabular-nums',
            NIVELES[nivel].texto,
            nivel === 'NEUTRO' && 'text-texto',
          )}
        >
          {valor}
        </span>
        {unidad && <span className="text-xs text-texto-mute">{unidad}</span>}
      </div>
      {detalle && <div className="mt-1 text-xs text-texto-mute">{detalle}</div>}
    </Tarjeta>
  );
}

// ── Estado vacío ──────────────────────────────────────────────────────────
export function Vacio({
  icono,
  titulo,
  children,
}: {
  icono?: ReactNode;
  titulo: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {icono && <div className="text-texto-mute/40">{icono}</div>}
      <p className="text-sm font-medium text-texto">{titulo}</p>
      {children && <p className="max-w-sm text-xs text-texto-mute">{children}</p>}
    </div>
  );
}

/** Etiqueta de estado de guía. */
export function EstadoGuiaChip({ estado }: { estado: string }) {
  const mapa: Record<string, { nivel: Nivel; texto: string }> = {
    BORRADOR: { nivel: 'AMARILLO', texto: 'Borrador' },
    EMITIDA: { nivel: 'VERDE', texto: 'Emitida' },
    FACTURADA: { nivel: 'VERDE', texto: 'Facturada' },
    ANULADA: { nivel: 'ROJO', texto: 'Anulada' },
    REQUIERE_CLASIFICACION: { nivel: 'AMARILLO', texto: 'Por clasificar' },
  };
  const e = mapa[estado] ?? { nivel: 'NEUTRO' as Nivel, texto: estado };
  return <Chip nivel={e.nivel}>{e.texto}</Chip>;
}
